// ============================================================
// FILE: SubmitQuote.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\SubmitQuote.js
// ============================================================
// PURPOSE:
//   Lets a logged-in gardener submit a price quote on a job
//   they've been matched to. Shows the job summary (description,
//   photos, skills, budget) above the quote form for context.
//
// ROUTE: /gardener/job/:id/quote   (id = job_id)
//
// CALLS API:
//   GET  /api/jobs/:id        — load job details to display
//   POST /api/quotes          — submit the quote
//       body: { job_id, quoted_price, estimated_hours, proposed_date, notes }
//
// USED BY:
//   GardenerDashboard.js links here via the "Submit Quote" button
//   on each matched job card.
//
// NOTES FOR MAINTAINERS:
//   - quoted_price is required; estimated_hours/proposed_date/notes are optional
//   - The underlying stored proc (usp_quote_submit) enforces business rules:
//     gardener must be matched, job must still accept quotes, no duplicate
//     quotes, and monthly quote limits for Basic-plan gardeners.
//   - On success we redirect straight to /gardener/dashboard rather than
//     showing a confirmation page — keeps the flow fast.
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import client from '../../api/client';

export default function SubmitQuote() {
    const { id }   = useParams(); // job_id from the URL
    const navigate = useNavigate();

    // Job being quoted on — loaded once on mount
    const [job,     setJob]     = useState(null);
    const [loading, setLoading] = useState(true);

    // Form submission state
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');

    // Quote form fields
    const [form, setForm] = useState({
        quoted_price:    '',
        estimated_hours: '',
        proposed_date:   '',
        notes:            '',
    });

    // Load the job details once when the page mounts
    useEffect(() => {
        client.get(`/jobs/${id}`)
            .then(r => setJob(r.data.job))
            .catch(err => setError(err.response?.data?.error || 'Failed to load job.'))
            .finally(() => setLoading(false));
    }, [id]);

    // Submit the quote to the API
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            await client.post('/quotes', {
                job_id:          parseInt(id),
                quoted_price:    parseFloat(form.quoted_price),
                estimated_hours: form.estimated_hours ? parseInt(form.estimated_hours) : null,
                proposed_date:   form.proposed_date || null,
                notes:           form.notes || null,
            });
            // Quote submitted — go back to the job feed
            navigate('/gardener/dashboard');
        } catch (err) {
            // Surface the real error from the stored proc (e.g. "already quoted",
            // "quote limit reached") rather than a generic message
            setError(err.response?.data?.error || 'Failed to submit quote.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.loading}>Loading job...</div>;
    if (!job)    return <div style={styles.loading}>{error} <Link to="/gardener/dashboard">Back</Link></div>;

    return (
        <div style={styles.page}>
            <div style={styles.backRow}>
                <Link to="/gardener/dashboard" style={styles.back}>← Back to My Jobs</Link>
            </div>

            <h1 style={styles.title}>Submit a Quote</h1>

            {/* ── Job summary card — gives the gardener context before quoting ── */}
            <div style={styles.jobCard}>
                <div style={styles.jobCardHeader}>
                    <h3 style={styles.jobTitle}>{job.title}</h3>
                    <span style={styles.suburb}>📍 {job.suburb_name} {job.postcode}</span>
                </div>
                <p style={styles.jobDesc}>{job.description}</p>
                <div style={styles.jobMeta}>
                    {job.preferred_date && <span>📅 Preferred: {new Date(job.preferred_date).toLocaleDateString()}</span>}
                    {job.budget && <span>💰 Buyer's budget: ${job.budget}</span>}
                </div>
                {/* Skills the buyer tagged on the job */}
                {job.skills && job.skills.length > 0 && (
                    <div style={styles.chips}>
                        {job.skills.map(s => <span key={s.skill_id} style={styles.chip}>{s.skill_name}</span>)}
                    </div>
                )}
                {/* Job photos, if the buyer uploaded any */}
                {job.photos && job.photos.length > 0 && (
                    <div style={styles.photoGrid}>
                        {job.photos.map(p => (
                            <img key={p.photo_id} src={p.storage_url} alt={p.caption || 'Job photo'}
                                 style={styles.photo} onError={e => e.target.style.display='none'} />
                        ))}
                    </div>
                )}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {/* ── Quote form ── */}
            <form onSubmit={handleSubmit} style={styles.form}>
                <h3 style={styles.formTitle}>Your Quote</h3>

                <div style={styles.row}>
                    <div style={styles.field}>
                        <label style={styles.label}>Your price ($) *</label>
                        <input
                            type="number" min="1" step="1"
                            value={form.quoted_price}
                            onChange={e => setForm({...form, quoted_price: e.target.value})}
                            style={styles.input}
                            placeholder="e.g. 150"
                            required
                        />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Estimated hours</label>
                        <input
                            type="number" min="0.5" step="0.5"
                            value={form.estimated_hours}
                            onChange={e => setForm({...form, estimated_hours: e.target.value})}
                            style={styles.input}
                            placeholder="e.g. 3"
                        />
                    </div>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>When can you do this job?</label>
                    <input
                        type="date"
                        value={form.proposed_date}
                        onChange={e => setForm({...form, proposed_date: e.target.value})}
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Message to buyer</label>
                    <textarea
                        value={form.notes}
                        onChange={e => setForm({...form, notes: e.target.value})}
                        style={{...styles.input, height:'100px', resize:'vertical'}}
                        placeholder="Introduce yourself, mention your equipment, ask any questions..."
                    />
                </div>

                <div style={styles.tip}>
                    💡 Tip: Quotes with a personal message and clear timeline get accepted more often.
                </div>

                <div style={styles.btnRow}>
                    <Link to="/gardener/dashboard" style={styles.btnCancel}>Cancel</Link>
                    <button type="submit" style={styles.btnSubmit} disabled={saving}>
                        {saving ? 'Submitting...' : 'Submit Quote'}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ── Inline styles (kept local to this component) ──────────
const styles = {
    page:          { maxWidth:'700px', margin:'0 auto', padding:'32px 24px' },
    loading:       { textAlign:'center', padding:'60px', color:'#666' },
    backRow:       { marginBottom:'16px' },
    back:          { color:'#2d6a4f', fontSize:'0.9rem' },
    title:         { color:'#2d6a4f', marginBottom:'20px' },
    jobCard:       { background:'white', borderRadius:'10px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'20px' },
    jobCardHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px', flexWrap:'wrap', gap:'8px' },
    jobTitle:      { margin:0, color:'#222', fontSize:'1.1rem' },
    suburb:        { color:'#666', fontSize:'0.85rem' },
    jobDesc:       { color:'#444', fontSize:'0.92rem', lineHeight:'1.5', marginBottom:'10px' },
    jobMeta:       { display:'flex', gap:'16px', fontSize:'0.85rem', color:'#555', marginBottom:'10px', flexWrap:'wrap' },
    chips:         { display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'10px' },
    chip:          { padding:'3px 10px', borderRadius:'10px', backgroundColor:'#e8f5e9', color:'#2d6a4f', fontSize:'0.78rem' },
    photoGrid:     { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'6px', marginTop:'8px' },
    photo:         { width:'100%', height:'70px', objectFit:'cover', borderRadius:'4px' },
    error:         { backgroundColor:'#fee', border:'1px solid #fcc', borderRadius:'6px', padding:'10px', color:'#c00', marginBottom:'16px' },
    form:          { background:'white', borderRadius:'10px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
    formTitle:     { color:'#2d6a4f', marginBottom:'16px', fontSize:'1.1rem' },
    row:           { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
    field:         { marginBottom:'16px' },
    label:         { display:'block', fontWeight:'600', marginBottom:'6px', color:'#333', fontSize:'0.9rem' },
    input:         { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'1rem', boxSizing:'border-box' },
    tip:           { backgroundColor:'#fff3cd', border:'1px solid #ffc107', borderRadius:'6px', padding:'10px 14px', fontSize:'0.85rem', color:'#856404', marginBottom:'16px' },
    btnRow:        { display:'flex', gap:'12px', justifyContent:'flex-end' },
    btnCancel:     { padding:'10px 24px', border:'1px solid #ddd', borderRadius:'6px', color:'#666', textDecoration:'none', fontSize:'0.95rem' },
    btnSubmit:     { padding:'10px 28px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'6px', fontSize:'0.95rem', fontWeight:'bold', cursor:'pointer' },
};
